/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import { toast } from 'react-toastify';
import cloneDeep from 'lodash/cloneDeep';

import { noQuickEditField, checkCalculation, checkLogic } from '../../../shared/utils';
import { quickEdit, doSetActiveMobileField } from '../reducers/fieldSettings';
import { fetchTrueForm } from './form';

import {
  fieldsWithTextAsLabel,
  createControlForType,
  sendFieldToIntercom,
  rowAllowedFields,
  createTableProps,
  planSupportField,
  paymentFields,
  ControlType,
} from '../../../shared/form-lib';

import {
  doShowCtrlCalculationsModal,
  processNewRowControlTemp,
  doShowCtrlMultipleModal,
  doShowUpgradePlanModal,
  removeCtrlCalculations,
  moveFieldFromRowToRow,
  processNewRowControl,
  moveFieldInRowToPage,
  reorderPageElements,
  showCtrlRulesModal,
  processNewControl,
  moveFieldIntoRow,
  deleteRowElement,
  duplicateElement,
  removeCtrlRules,
  deleteElement,
  saveFormInfo,
  setRowId,
} from '../reducers/builder';

export const getUpdatedPipedLabelsMap = (props) => {
  const { prevLabelPipedCtrlLabelsMap, isDuplicateAction = false, isPageLoadAction = false, pages } = props;
  const prevPipedCtrlIds = Object.keys(prevLabelPipedCtrlLabelsMap);
  const labelPipedCtrlLabelsMap = {};
  
  const newPages = cloneDeep(pages).map((page) => (
    Object.fromEntries(Object.entries(page).map(([ctrlId, ctrl]) => {
      if (ctrl.type === ControlType.TABLE_FIELD) {
        const isNewTableCtrl = !prevPipedCtrlIds.includes(ctrlId);
        const pipedColumnHeaders = [];

        ctrl.table_props.map((tableCtrl, colIndex) => {
          const isNewColCtrl = !prevLabelPipedCtrlLabelsMap?.[ctrlId]?.[colIndex];
          const isNew = !isDuplicateAction && (isNewTableCtrl || isNewColCtrl);
          
          if (!tableCtrl.piped_label || (!isPageLoadAction && isNew)) {
            tableCtrl.piped_label = ctrl.props.column_headers[colIndex];
          }

          pipedColumnHeaders.push(tableCtrl.piped_label);
        });

        labelPipedCtrlLabelsMap[ctrlId] = pipedColumnHeaders;
      } else {
        if (!ctrl.props.piped_label) {
          const labelProp = fieldsWithTextAsLabel.includes(ctrl.type) ? 'text' : 'label';
          ctrl.props.piped_label = ctrl.props[labelProp];
        }

        labelPipedCtrlLabelsMap[ctrlId] = ctrl.props.piped_label;
      }

      return [ctrlId, ctrl];
    }))
  ));

  return { labelPipedCtrlLabelsMap, newPages };
};

export const inventoryControls =
  (inForm = false) =>
  (dispatch, getState) => {
    const { builder, form } = getState();
    let ctrls = [];

    if (inForm) {
      ctrls = form.editableForm?.definition?.pages.flatMap(({ controls }) => controls);
    } else {
      const { internalIdentifiers } = builder?.present;
      ctrls = Object.values(internalIdentifiers || {});
    }

    return {
      inventory_payment: ctrls?.find(({ props }) => props.is_inventory_payment),
      inventory_field: ctrls?.find(({ type }) => type === 'inventory_field'),
    };
  };

/**
 * Action creator function to save form information.
 *
 * @param {string} name - The property of the form to update (mostly 'name').
 * @param {string} value - The new name of the form to save.
 * @returns {function} A Redux thunk function that dispatches an action to save the form information.
 */
export const doSaveFormInfo = (name, value) => (dispatch) => {
  dispatch(saveFormInfo({ name, value }));
};

/**
 * Prevents duplicate candidate IDs by incrementing the ID if it already exists in the identifiers object.
 *
 * @param {number} candidateId - The candidate ID to be checked and potentially incremented.
 * @param {Object} identifiers - An object containing existing candidate IDs as keys.
 * @returns {number} The non-duplicate candidate ID, possibly incremented.
 */
export const blockDuplicateId = (candidateId, identifiers) => {
  if (Object.keys(identifiers).length > 0) {
    while (candidateId.toString() in identifiers) {
      candidateId += 1;
    }
  }
  return candidateId;
};

/**
 * Creates a new identifier, ensuring it is not a duplicate in the provided identifiers object.
 *
 * @param {Object} identifiers - An object containing existing identifiers as keys.
 * @returns {string} A non-duplicate identifier.
 */
const doCreateIdentifier = (identifiers) => {
  let candidateId = Date.now();
  candidateId = blockDuplicateId(candidateId, identifiers).toString();
  return candidateId;
};

/**
 * Assigns identifiers and initializes properties for a control.
 *
 * @param {Object} control - The control object to be assigned identifiers and properties.
 * @param {Object} identifiers - An object containing existing identifiers as keys.
 * @param {boolean} newField - Indicates whether this is a new field (true) or not (false).
 * @returns {Object} A modified control object with assigned identifiers and initialized properties.
 */
const doAssignControlIdentifiers = (control, identifiers, newField) => {
  const internalId = doCreateIdentifier(identifiers);
  let newControl = cloneDeep(control);
  if (newField) {
    newControl.table_props = [];
    if (newControl.type === 'table_field') newControl.table_props = createTableProps(newControl.props.column_types);
  }
  newControl.internal_id = internalId;
  newControl.display_id = internalId;
  newControl.value = null;
  newControl._validationError = '';
  return newControl;
};

/**
 * Processes a new control, assigns identifiers, and dispatches actions accordingly.
 *
 * @param {Object} control - The control object to be processed.
 * @param {Object} result - The drag event result associated with the control created.
 * @param {boolean} clicked - Indicates whether the control was clicked.
 * @returns {Function} A Redux thunk function that processes the new control and dispatches relevant actions.
 */
export const doProcessNewControl = (control, result, clicked) => (dispatch, getState) => {
  const {
    builderForm: { identifiers },
    activePage: page,
    form,
  } = getState().builder.present;

  const inventoryCtrls = dispatch(inventoryControls());
  if (inventoryCtrls[control.type]) {
    return toast.warn('You can only have one inventory field per form!');
  }

  let newControl = {};

  if (!control.internal_id) {
    // Assign identifiers and create a new control
    newControl = doAssignControlIdentifiers(control, identifiers, true);

    // Dispatch actions for processing the new control
    dispatch(
      processNewControl({
        control: newControl,
        result,
        page,
        clicked,
      })
    );

    // Handle quick edit based on screen width and control type
    if (document.body.clientWidth >= 768 && !noQuickEditField(newControl.type)) {
      dispatch(quickEdit(newControl.internal_id));
    } else if (document.body.clientWidth >= 768 && noQuickEditField(newControl.type)) {
      dispatch(quickEdit(''));
    } else {
      dispatch(doSetActiveMobileField(newControl.internal_id));
    }

    if (!control.props.is_inventory_payment) toast.success('Field Added');

    sendFieldToIntercom(control.type, form?.id || null);
  }
};

/**
 * Processes a new row control, assigns identifiers, and dispatches actions accordingly.
 *
 * @param {Object} control - The row control object to be processed.
 * @param {Object} result - The drag event result associated with the control created.
 * @param {string} rowInternalId - The internal ID of the row to which the control is added.
 * @returns {Function} A Redux thunk function that processes the new row control and dispatches relevant actions.
 */
export const doProcessNewRowControl = (control, result, rowInternalId) => (dispatch, getState) => {
  const {
    activePage: page,
    builderForm: { identifiers, pages },
  } = getState().builder.present;

  let newControl;

  // Check if the control type is allowed for rows
  if (rowAllowedFields.includes(control.type)) {
    // Get the number of controls in the row
    const rowControl = pages[page][rowInternalId].props.controls.length;

    // Check if the row can have more controls (up to 3)
    if (rowControl < 3) {
      if (!control.internal_id) {
        // Assign identifiers and create a new row control
        newControl = doAssignControlIdentifiers(control, identifiers, true);

        // Dispatch actions for processing the new row control
        dispatch(
          processNewRowControl({
            control: newControl,
            result,
            page,
            rowInternalId,
          })
        );

        toast.success('Row Field Added');
      }
    } else {
      // Display an error toast if the row already has the maximum allowed controls
      toast.error('Maximum of 3 fields allowed in a row');
    }
  } else {
    // Display an error toast if the control type is not allowed in rows
    toast.error('Field not allowed in row field');
  }
};

/**
 * Processes a new row control, assigns identifiers, and dispatches actions accordingly.
 *
 * @param {Object} control - The row control object to be processed.
 * @param {string} rowInternalId - The internal ID of the row to which the control is added.
 * @returns {Function} A Redux thunk function that processes the new row control and dispatches relevant actions.
 */
export const doProcessNewRowControlTemp = (control, rowWrapperId) => (dispatch, getState) => {
  const {
    activePage: page,
    builderForm: { identifiers, pages },
  } = getState().builder.present;

  let newControl;

  // Get the number of controls in the row
  const rowControl = pages[page][rowWrapperId].props.controls.length;

  // Check if the row can have more controls (up to 3)
  if (rowControl < 3) {
    if (!control.internal_id) {
      // Assign identifiers and create a new row control
      newControl = doAssignControlIdentifiers(control, identifiers, true);

      // Dispatch actions for processing the new row control
      dispatch(
        processNewRowControlTemp({
          control: newControl,
          page,
          rowWrapperId,
        })
      );

      toast.success('Row Field Added');
    }
  } else {
    // Display an error toast if the row already has the maximum allowed controls
    toast.error('Maximum of 3 fields alowed');
  }
};

/**
 * Deletes a row element from a row control and dispatches the necessary action.
 *
 * @param {string} rowCtrlInternalId - The internal ID of the row element to be deleted.
 * @param {string} rowInternalId - The internal ID of the row container from which the element is deleted.
 * @param {number} index - The index of the element to be deleted within the row control.
 * @returns {Function} A Redux thunk function that deletes the row element and dispatches the relevant action.
 */
export const doDeleteRowElement = (rowCtrlInternalId, rowInternalId, index) => (dispatch, getState) => {
  const { activePage: page } = getState().builder.present;

  // Dispatch action to delete the row element
  dispatch(
    deleteRowElement({
      rowCtrlInternalId,
      rowInternalId,
      index,
      page,
    })
  );
};

/**
 * Moves a field out of a row into page and dispatches the necessary action.
 *
 * @param {Object} result - The drag event result associated with the field to be moved.
 * @param {string} rowInternalId - The internal ID of the row container from which the field is moved.
 * @returns {Function} A Redux thunk function that moves the field to another page and dispatches the relevant action.
 */
export const doMoveFieldInRowToPage = (result, rowInternalId) => (dispatch, getState) => {
  const { activePage: page } = getState().builder.present;

  // Dispatch action to move the field out of row to page
  dispatch(moveFieldInRowToPage({ result, rowInternalId, page }));
};

/**
 * Reorders page elements and dispatches the necessary action.
 *
 * @param {Object} result - The drag event result representing the reordered page elements.
 * @returns {Function} A Redux thunk function that reorders the page elements and dispatches the relevant action.
 */
export const doReorderPageElements = (result) => (dispatch) => {
  // Dispatch action to reorder page elements
  dispatch(
    reorderPageElements({
      result,
    })
  );
};

/**
 * Moves a field into a row and dispatches the necessary action if it's allowed and the row has available space.
 *
 * @param {Object} result - The drag event result representing the field being moved.
 * @param {string} rowInternalId - The internal ID of the row to which the field is moved.
 * @returns {Function} A Redux thunk function that moves the field into the row and dispatches the relevant action.
 */
export const doMoveFieldIntoRow = (result, rowInternalId) => (dispatch, getState) => {
  const {
    activePage: page,
    builderForm: { pages },
    internalIdentifiers,
  } = getState().builder.present;

  // Get the number of controls in the row
  const rowControl = pages[page][rowInternalId].props.controls.length;

  // Get the dragged field information
  const draggedField = internalIdentifiers[result.draggableId];

  // Check if the dragged field type is allowed in rows
  if (rowAllowedFields.includes(draggedField.type)) {
    // Check if the row has available space (up to 3 fields)
    if (rowControl < 3) {
      // Dispatch action to move the field into the row
      dispatch(
        moveFieldIntoRow({
          result,
          rowInternalId,
          page,
        })
      );
    } else {
      // Display an error toast if the row already has the maximum allowed fields
      toast.error('Maximum of 3 fields allowed in a row');
    }
  } else {
    // Display an error toast if the field type is not allowed in rows
    toast.error('Field not allowed in row field');
  }
};

/**
 * Moves a field from one row to another row and dispatches the necessary action if the destination row has available space.
 *
 * @param {Object} result - The drag event result representing the field being moved.
 * @param {string} destRowInternalId - The internal ID of the destination row where the field is moved.
 * @param {string} sourceRowInternalId - The internal ID of the source row from which the field is moved.
 * @returns {Function} A Redux thunk function that moves the field from one row to another and dispatches the relevant action.
 */
export const doMoveFieldFromRowToRow = (result, destRowInternalId, sourceRowInternalId) => (dispatch, getState) => {
  const {
    activePage: page,
    builderForm: { pages },
  } = getState().builder.present;

  // Get the number of controls in the destination row
  const destRowControl = pages[page][destRowInternalId].props.controls.length;

  // Check if the destination row has available space (up to 3 fields)
  if (destRowControl < 3) {
    // Dispatch action to move the field from one row to another
    dispatch(moveFieldFromRowToRow({ result, destRowInternalId, sourceRowInternalId, page }));
  } else {
    // Display an error toast if the destination row already has the maximum allowed fields
    toast.error('Maximum of 3 fields allowed in a row');
  }
};

/**
 * Duplicates a control element, assigns identifiers, and dispatches the necessary actions.
 *
 * @param {Object} control - The control element to be duplicated.
 * @param {number} index - The index of the control in its parent container.
 * @returns {Function} A Redux thunk function that duplicates the control element, assigns identifiers, and dispatches relevant actions.
 */
export const doDuplicateElement = (control, index) => (dispatch, getState) => {
  const {
    activePage: page,
    builderForm: { identifiers },
    addRowCtrls,
  } = getState().builder.present;

  const inventoryCtrls = dispatch(inventoryControls());
  if (inventoryCtrls[control.type]) {
    return toast.warn('You can only have one inventory field per form!');
  }

  let newControl = {};

  // Assign identifiers and create a duplicate control
  newControl = doAssignControlIdentifiers(control, identifiers);

  // If duplicated field is a row field, create duplicates for each control inside it
  const newInnerCtrls = [];
  const innerCtrlIds = control.props.controls;

  if (control.type === 'add_row' && innerCtrlIds.length) {
    const newInnerCtrlIds = [];

    for (let i = 0; i < innerCtrlIds.length; i += 1) {
      const newCtrl = cloneDeep(addRowCtrls[innerCtrlIds[i]]);

      // Generate a random ID add-on to avoid duplicates
      const idAddOn = Math.floor(Math.random() * (999 - 199 + 1)) + 199;
      newCtrl.internal_id = Number(newCtrl.internal_id) + idAddOn;

      // Prevent duplicate IDs
      newCtrl.internal_id = blockDuplicateId(newCtrl.internal_id, identifiers);
      newCtrl.internal_id = newCtrl.internal_id.toString();
      newCtrl.display_id = newCtrl.internal_id;

      newInnerCtrlIds.push(newCtrl.internal_id);
      newInnerCtrls.push(newCtrl);
    }

    newControl.props.controls = newInnerCtrlIds;
  }

  // Dispatch action to duplicate the control element
  dispatch(
    duplicateElement({
      control: newControl,
      page,
      index,
      newInnerCtrls,
    })
  );

  // Handle quick edit based on screen width and control type
  if (document.body.clientWidth >= 768 && !noQuickEditField(newControl.type)) {
    dispatch(quickEdit(newControl.internal_id));
  } else if (document.body.clientWidth >= 768 && noQuickEditField(newControl.type)) {
    dispatch(quickEdit(''));
  } else {
    dispatch(doSetActiveMobileField(newControl.internal_id));
  }

  toast.success('Field Duplicated');
};

/**
 * Removes a control and its associated rules, and dispatches the necessary actions.
 *
 * @returns {Function} A Redux thunk function that removes the control and its rules, and dispatches relevant actions.
 */
export const doRemoveCtrlRules = () => (dispatch, getState) => {
  const { activePage: page, ctrlToBeRmvd: control, builderForm, rowId } = getState().builder.present;
  const { rules } = builderForm;

  // Check and retrieve rules attached to the control
  const attachedRules = checkLogic(rules, control);

  // Reverse the attached rules to remove them in the correct order
  attachedRules.reverse();

  // NOTE: DISPATCH ORDER BELOW IS IMPORTANT !!!..
  // Attached rule(s) should be removed before deleting the field,
  // This will ensure a proper undo-redo flow

  // Dispatch action to remove the attached rules
  // dispatch(removeCtrlRules(attachedRules));

  // Dispatch action to delete the control
  dispatch(deleteElement({ control, page, rowId }));

  toast.success('Field Deleted');
};

/**
 * Removes a control and its associated calculations, and dispatches the necessary actions.
 *
 * @returns {Function} A Redux thunk function that removes the control and its calculations, and dispatches relevant actions.
 */
export const doRemoveCtrlCalculations = () => (dispatch, getState) => {
  const { activePage: page, ctrlToBeRmvd: control, builderForm, rowId } = getState().builder.present;
  const { calculations } = builderForm;

  // Check and retrieve calculations attached to the control
  const attachedCals = checkCalculation(calculations, control);

  // Reverse the attached calculations to remove them in the correct order
  attachedCals.reverse();

  // NOTE: DISPATCH ORDER BELOW IS IMPORTANT !!!..
  // Attached calculation(s) should be removed before deleting the field,
  // This will ensure a proper undo-redo flow

  // Dispatch action to remove the attached calculations
  dispatch(removeCtrlCalculations(attachedCals));

  // Dispatch action to delete the control
  dispatch(deleteElement({ control, page, rowId }));

  toast.success('Field Deleted');
};

/**
 * Removes multiple elements (e.g., rules and calculations) associated with a control, and dispatches the necessary actions.
 *
 * @returns {Function} A Redux thunk function that removes multiple elements and dispatches relevant actions.
 */
export const doRemoveMultiples = () => (dispatch, getState) => {
  const { multiples } = getState().builder.present;

  // if(multiples.includes('lookups')){

  // }

  // Check if 'rules' is among the multiples and remove control rules
  if (multiples.includes('rules')) {
    dispatch(doRemoveCtrlRules());
  }

  // Check if 'calculations' is among the multiples and remove control calculations
  if (multiples.includes('calculations')) {
    dispatch(doRemoveCtrlCalculations());
  }

  // Close the control multiple modal
  dispatch(doShowCtrlMultipleModal({ status: false, control: null, multiples: null }));
};

// export const doRemoveElement = () => (dispatch, getState) => {
//   const { activePage: page, ctrlToBeRmvd: control, rowId } = getState().builder.present;

//   // Dispatch action to delete the control
//   // This action also deletes any attached rule triggers, actions or calculations
//   dispatch(deleteElement({ control, page, rowId }));

//   toast.success('Field Deleted');
// }

/**
 * Handles attached multiple elements (e.g., rules and calculations) for a control and dispatches the necessary actions.
 *
 * @param {Map} attached - A Map containing attached multiple elements.
 * @param {Object} control - The control to which the elements are attached.
 * @returns {Function} A Redux thunk function that handles attached multiple elements and dispatches relevant actions.
 */
const handleAttachedMultiple = (attached, control) => (dispatch) => {
  // Get an array of keys from the Map
  const mapkeys = Array.from(attached.keys());

  // Check the number of attached elements
  if (mapkeys.length === 1) {
    // Handle the case when there's only one attached element
    switch (mapkeys[0]) {
      case 'rules':
        dispatch(showCtrlRulesModal({ status: true, control }));
        break;
      case 'calculations':
        dispatch(doShowCtrlCalculationsModal({ status: true, control }));
        break;
      // case 'lookups':
      //   break;
      default:
    }
  } else if (mapkeys.length > 1) {
    // Handle the case when there are multiple attached elements
    dispatch(doShowCtrlMultipleModal({ status: true, control, multiples: mapkeys }));
  }
};

/**
 * Deletes a control element, its associated rules and calculations, and dispatches the necessary actions.
 *
 * @param {Object} control - The control element to be deleted.
 * @param {string} rowId - The ID of the row containing the control (optional).
 * @returns {Function} A Redux thunk function that deletes the control element and its associated elements, and dispatches relevant actions.
 */
export const doDeleteElement = (control, rowId) => (dispatch, getState) => {
  const { activePage: page, builderForm } = getState().builder.present;
  const { rules, calculations } = builderForm;

  // Check and retrieve rules and calculations attached to the control
  const rulesAttached = checkLogic(rules, control);
  const calculationsAttached = checkCalculation(calculations, control);

  // Create a Map to store attached elements
  const attached = new Map();

  // Set the row ID if provided
  if (rowId) dispatch(setRowId(rowId));

  // Check if there are attached rules, calculations, or other elements (e.g., lookups)
  if (rulesAttached.length) attached.set('rules', rulesAttached);
  if (calculationsAttached.length) attached.set('calculations', calculationsAttached);
  // Todo: Add check for lookup

  // Get the number of attached elements
  const attachLength = Array.from(attached.keys()).length;

  if (attachLength === 0) {
    if (control.type === 'inventory_field') {
      const { inventory_payment } = dispatch(inventoryControls());
      if (inventory_payment) {
        dispatch(doDeleteElement(inventory_payment, null));
      }
    }
    
    // If there are no attached elements, directly delete the control
    dispatch(
      deleteElement({
        control,
        page,
        rowId,
      })
    );

    if (!control.props.is_inventory_payment) toast.success('Field Deleted');
  } else {
    // If there are attached elements, handle them based on their type
    dispatch(handleAttachedMultiple(attached, control, rowId));
  }
};

export const createNewInventoryPaymentControl = (type, currency) => async (dispatch) => {
  if (planSupportField(type)) {
    const canCreate = await dispatch(validatePaymentFieldAddition(type));
    if (!canCreate) return;

    const props = { ...createControlForType(type) };
  
    props.label = `${props.label} (Inventory)`;
    props.is_inventory_payment = true;
    props.currency = currency;
  
    const ctrl = { props, type };
    dispatch(doProcessNewControl(ctrl, {}, true));
  } else {
    dispatch(doShowUpgradePlanModal(true));
  }
};

export const validatePaymentFieldAddition = (type) => async (dispatch, getState) => {
  const { isTemplateForm, isNewForm, form } = getState().builder.present;

  if (isTemplateForm || isNewForm) return true;
  if (!form?.id || !paymentFields.includes(type)) return true;

  const trueForm = await fetchTrueForm(form.id);
  const isSubmitDisabled = trueForm?.options?.hide_submit_button;

  if (isSubmitDisabled) {
    toast.error('Payment fields cannot be added when the submit button is disabled');
    return false;
  }

  return true;
};

/**
 * Creates a new control or shows the upgrade modal if plan checks return falsey.
 *
 * @param {string} type - The type of field to be created.
 */
export const createNewControlOnClick = (type) => async (dispatch) => {
  if (planSupportField(type)) {
    const canCreate = await dispatch(validatePaymentFieldAddition(type));
    if (!canCreate) return;

    const props = { ...createControlForType(type) };
    if (type === 'embed_field') props.height = 480;
    const ctrl = { props, type };
    dispatch(doProcessNewControl(ctrl, {}, true));
  } else {
    dispatch(doShowUpgradePlanModal(true));
  }
};

export const isPipedCtrl = (ctrlId) => (dispatch, getState) => {
  const formControls = getState().builder.present.builderForm.pages.flatMap((page) => Object.values(page));

  return formControls.some((control) => {
    if (control.internal_id === ctrlId) return false;

    if (control.type === ControlType.TABLE_FIELD) {
      return control.props.column_headers.some((header) => header?.includes(ctrlId));
    }

    const fieldLabelProp = fieldsWithTextAsLabel.includes(control.type) ? 'text' : 'label';
    const fieldLabel = control.props[fieldLabelProp];
    return fieldLabel?.includes(ctrlId);
  });
}
